import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Button} from 'views/components';
import {AddCircleOutline as AddCircleOutlineIcon} from 'views/icons';
import {Translate} from 'views/module';

import RelationshipNew from './RelationshipNew';
import RelationshipView from './RelationshipView';
import RelationshipEdit from './RelationshipEdit';

import {convertToE164} from 'utils/miscellaneous/e164';

const RelationshipList = (props) => {
  const [model, setModel] = React.useState({});

  const clientData = props.clientData;
  const clientAuthentication = props.clientAuthentication;
  const clientBackend = props.clientBackend;
  const relationships = clientData.relationships;
  const clientNotificationChannels = props.clientNotificationChannels;
  const clientCommunication = props.clientCommunication;
  const clientDialpadIntegration = props.clientDialpadIntegration;

  const openDialogs = {
    close: () => {
      setModel({});
    },
    addRelationship: () => {
      setModel({action: 'ADD_RELATIONSHIP'});
    },
    editRelationship: (relationship) => {
      setModel({action: 'EDIT_RELATIONSHIP', relationship});
    },
  };

  const edits = {
    add: (relationship) => {
      const complete = () => {
        setModel({});
      };

      const input = {
        name: relationship.name,
        connectionType: relationship.connectionType,
        priority: relationship.priority,
        description: relationship.description,
        contacts: relationship.contacts
            .filter((e) => e.type && e.detail)
            .map((e) => {
              const contact = {...e};
              if (contact.type === 'Phone') {
                const conversion = convertToE164(contact.detail);
                if (conversion.isValid) {
                  contact.detail = conversion.e164Format;
                } else {
                  contact.detail = contact.detail.trim();
                }
              }
              return contact;
            }),
      };
      clientBackend.detail.relationship.add(input, complete);
    },
    edit: (relationshipRn, relationship) => {
      const complete = () => {
        setModel({});
      };

      const input = {
        name: relationship.name,
        connectionType: relationship.connectionType,
        priority: relationship.priority,
        description: relationship.description,
        contacts: relationship.contacts
            .filter((e) => e.type && e.detail)
            .map((e) => {
              const contact = {...e};
              if (contact.type === 'Phone') {
                const conversion = convertToE164(contact.detail);
                if (conversion.isValid) {
                  contact.detail = conversion.e164Format;
                } else {
                  contact.detail = contact.detail.trim();
                }
              }
              return contact;
            }),
      };
      clientBackend.detail.relationship.edit(relationshipRn, input, complete);
    },
    remove: (relationshipRn) => {
      const complete = () => {
        setModel({});
      };

      clientBackend.detail.relationship.remove(relationshipRn, complete);
    },
  };

  const popModelGenerator = React.useCallback(() => {
    let popModel = null;
    if (model.action === 'ADD_RELATIONSHIP') {
      popModel = <RelationshipNew
        onClose={openDialogs.close}
        addRelationship={edits.add}
      />;
    } else if (model.action === 'EDIT_RELATIONSHIP') {
      popModel = <RelationshipEdit
        onClose={openDialogs.close}
        editRelationship={edits.edit}
        relationship={model.relationship}
      />;
    }
    return popModel;
  }, [model]);


  const calculateRelationships = React.useCallback(() => {
    return Object.values(relationships).sort((a, b) => a.priority - b.priority);
  }, [relationships]);

  const relationshipView = calculateRelationships();
  return (
    <Stack
      sx={{
      }}
    >
      {popModelGenerator()}
      {
        relationshipView.map((e, index) => {
          const userIdentity = clientAuthentication[e.relationshipRn];
          const userNotificationChannels = clientNotificationChannels[e.relationshipRn];
          const userCommunication = clientCommunication[e.relationshipRn];
          const userDialpadIntegration = clientDialpadIntegration[e.relationshipRn];
          return (
            <RelationshipView
              key={index}
              relationshipData={e}
              removeRelationship={edits.remove}
              editRelationship={openDialogs.editRelationship}
              clientBackend={clientBackend}
              userIdentity={userIdentity}
              userNotificationChannels={userNotificationChannels}
              userCommunication={userCommunication}
              userDialpadIntegration={userDialpadIntegration}
              clientAuthenticationLoading={clientAuthentication[e.relationshipRn] === undefined}
              clientNotificationLoading={userNotificationChannels === undefined}
              clientCommunicationLoading={clientCommunication[e.relationshipRn] === undefined}
              clientDialpadIntegrationLoading={clientDialpadIntegration[e.relationshipRn] === undefined}
            />
          );
        })
      }
      <Button
        startIcon={<AddCircleOutlineIcon />}
        onClick={openDialogs.addRelationship}
        sx={{
          mx: 2,
          my: 1,
        }}
      >
        <Translate expression='client.ClientDetail.Relationships.AddRelationship' />
      </Button>
    </Stack>
  );
};

RelationshipList.propTypes = {
  clientData: PropTypes.object.isRequired,
  clientBackend: PropTypes.object.isRequired,
  clientAuthentication: PropTypes.object.isRequired,
  clientNotificationChannels: PropTypes.objectOf(
      PropTypes.shape({
        emails: PropTypes.array,
        smsNumbers: PropTypes.array,
      }),
  ).isRequired,
  clientCommunication: PropTypes.object.isRequired,
  clientDialpadIntegration: PropTypes.object.isRequired,
};
export default React.memo(RelationshipList);
